import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, AlertCircle, CheckCircle, Loader2, Eye, EyeOff, Shield } from 'lucide-react';

const ForcePasswordChangePage = () => {
  const { user, refreshAuth } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!form.current_password || !form.new_password || !form.confirm_password) {
      setError('All fields are required');
      return;
    }
    if (form.new_password.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }
    if (form.new_password !== form.confirm_password) {
      setError('New passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('ihostpms_token')}`
        },
        body: JSON.stringify({
          current_password: form.current_password,
          new_password: form.new_password
        })
      });

      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data.detail || 'Failed to change password');
      }

      // Refresh user data and redirect to dashboard
      await refreshAuth();
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-airbnb-light-gray to-white px-4" data-testid="force-password-change-page">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-amber-600" />
            </div>
            <h1 className="text-2xl font-bold text-airbnb-gray">Password Change Required</h1>
            <p className="text-airbnb-medium-gray mt-2">
              For security reasons, you must change your temporary password before continuing.
            </p>
          </div>

          {user && (
            <div className="bg-slate-50 rounded-lg p-3 mb-6 text-center">
              <p className="text-sm text-slate-600">Logged in as <strong>{user.email}</strong></p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label className="text-airbnb-gray font-medium text-sm mb-1.5 block">Current/Temporary Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-airbnb-medium-gray" />
                <Input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={form.current_password}
                  onChange={(e) => setForm({ ...form, current_password: e.target.value })}
                  className="pl-10 pr-10 h-12 border-gray-200 focus:border-airbnb-pink focus:ring-airbnb-pink/20"
                  placeholder="Enter your temporary password"
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label className="text-airbnb-gray font-medium text-sm mb-1.5 block">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-airbnb-medium-gray" />
                <Input
                  type={showNewPassword ? 'text' : 'password'}
                  value={form.new_password}
                  onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                  className="pl-10 pr-10 h-12 border-gray-200 focus:border-airbnb-pink focus:ring-airbnb-pink/20"
                  placeholder="Create a new password"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Minimum 8 characters</p>
            </div>

            <div>
              <Label className="text-airbnb-gray font-medium text-sm mb-1.5 block">Confirm New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-airbnb-medium-gray" />
                <Input
                  type="password"
                  value={form.confirm_password}
                  onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                  className="pl-10 h-12 border-gray-200 focus:border-airbnb-pink focus:ring-airbnb-pink/20"
                  placeholder="Confirm your new password"
                  data-testid="confirm-password-input"
                />
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg" data-testid="error-message">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-airbnb-pink hover:bg-airbnb-dark-pink text-white font-semibold text-base rounded-xl transition-colors"
              data-testid="submit-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Set New Password & Continue
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-700">
              <strong>Why am I seeing this?</strong><br />
              You're using a temporary password that was sent when your account was created. 
              For security, you need to set your own password.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForcePasswordChangePage;
